import classNames from "classnames";
import { Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Resizable, ResizeCallbackData } from "react-resizable";
import { lazyWithPreload } from "utils/lazyWithPreload";

import { useEnableConnectWizard } from "featureFlags/hooks/useEnableConnectWizard";
import { usePreloadComponents } from "hooks/usePreloadedComponents";

import DocumentObjectExplorerCollapseIcon from "assets/icons/DocumentObjectExplorerCollapseIcon.svg?react";
import DocumentOutputCollpaseIcon from "assets/icons/DocumentOutputCollpaseIcon.svg?react";

import useActiveDocument from "pages/DevelopWorkspace/contexts/DocumentsContext/hooks/useActiveDocument";
import { SHORTCUTS } from "pages/DevelopWorkspace/services/shortcuts/constants";
import { getShortcutHint } from "pages/DevelopWorkspace/services/shortcuts/helpers/getShortcutHint";
import { ShortcutAction } from "pages/DevelopWorkspace/services/shortcuts/types";

import Button, { ButtonSize, ButtonTemplate } from "components/Button/Button";
import { ConnectIcon } from "components/Icons";
import LoadingOverlap from "components/LoadingOverlap";
import Tooltip from "components/Tooltip";

import { useLayoutToggles } from "../LayoutToggles/context";
import { DatabasesPanel } from "./DatabasesPanel/DatabasesPanel";
import { Header } from "./Header";
import { LeftSidebarContext, useLeftSidebarState } from "./context";
import { LeftSidebarTabs } from "./types";

import styles from "./styles.module.scss";

const EnginesPanel = lazyWithPreload(
  () => import("./EnginesPanel/EnginesPanel")
);
const SecurityPanel = lazyWithPreload(
  () => import("./SecurityPanel/SecurityPanel")
);

const ConnectModalLoader = lazyWithPreload(
  () => import("./ConnectModal/ConnectModalLoader")
);

const tabPanels = {
  [LeftSidebarTabs.DATABASES]: DatabasesPanel,
  [LeftSidebarTabs.ENGINES]: EnginesPanel,
  [LeftSidebarTabs.SECURITY]: SecurityPanel,
};

const LEFT_MAX_SIDEBAR = 500;
const LEFT_MIN_SIDEBAR = 280;
const LEFT_DEFAULT_SIDEBAR = 400;
const ENGINES_EXPANDED_WIDTH = 515;

const LeftSidebar = () => {
  const [isResizing, setIsResizing] = useState(false);
  const [tab, setTab] = useState(LeftSidebarTabs.DATABASES);
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);

  const { layout, setLayout } = useLayoutToggles();
  const [width, setWidth] = useState(
    layout.leftSidebar?.width || LEFT_DEFAULT_SIDEBAR
  );
  const { leftSidebarContext } = useLeftSidebarState({ setWidth });
  const activeDocument = useActiveDocument();
  const isConnectWizardEnabled = useEnableConnectWizard();

  const { t } = useTranslation();

  usePreloadComponents([EnginesPanel, SecurityPanel, ConnectModalLoader]);

  useEffect(() => {
    if (tab === LeftSidebarTabs.ENGINES && layout.engines?.expanded) {
      setWidth(ENGINES_EXPANDED_WIDTH);
    } else {
      setWidth(layout.leftSidebar?.width || LEFT_DEFAULT_SIDEBAR);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout.engines?.expanded, tab]);

  const TabPanel = tabPanels[tab];

  const maxConstraint = layout.engines?.expanded
    ? ENGINES_EXPANDED_WIDTH
    : LEFT_MAX_SIDEBAR;

  const minConstraint = layout.engines?.expanded
    ? ENGINES_EXPANDED_WIDTH
    : LEFT_MIN_SIDEBAR;

  const handleResize = (
    event: React.SyntheticEvent,
    { size }: ResizeCallbackData
  ) => {
    if (layout.engines?.expanded) {
      return;
    }
    if (!isResizing) {
      setIsResizing(true);
    }
    setWidth(size.width);
  };

  const handleResizeStop = (
    event: React.SyntheticEvent,
    data: ResizeCallbackData
  ) => {
    if (layout.engines?.expanded) {
      return;
    }
    if (isResizing) {
      setIsResizing(false);
    }
    setLayout({
      ...layout,
      leftSidebar: {
        ...layout.leftSidebar,
        width: data.size.width,
      },
    });
  };

  return (
    <div className={styles.wrapper}>
      <Resizable
        className={classNames(styles.drawer, {
          [styles.isResizing]: isResizing,
          [styles.hidden]: !layout.leftSidebar.expanded,
        })}
        width={width}
        height={window.innerHeight}
        onResize={handleResize}
        onResizeStop={handleResizeStop}
        minConstraints={[minConstraint, LEFT_DEFAULT_SIDEBAR]}
        maxConstraints={[maxConstraint, LEFT_MAX_SIDEBAR]}
        resizeHandles={["e"]}
        handle={
          <span
            data-testid="aqa-resize-handle-left"
            className={classNames(styles.customHandle)}
          />
        }
      >
        <div style={{ width }}>
          <LeftSidebarContext.Provider value={leftSidebarContext}>
            <Header
              activeTab={tab}
              setTab={setTab}
            />

            <Suspense
              fallback={
                <div style={{ height: "100%" }}>
                  <LoadingOverlap isLoading />
                </div>
              }
            >
              <TabPanel />
            </Suspense>

            <div
              className={classNames(styles.sidebarFooter, {
                [styles.expanded]: layout.leftSidebar.expanded,
              })}
            >
              <div
                className={classNames(styles.layoutToggles, {
                  [styles.expanded]: layout.leftSidebar.expanded,
                })}
              >
                <Tooltip
                  title={
                    layout.leftSidebar.expanded
                      ? t("workspace.tooltips.hide_sidebar")
                      : t("workspace.tooltips.show_sidebar")
                  }
                  placement="top-start"
                >
                  <div
                    className={classNames(styles.toggle, {
                      [styles.active]: layout.leftSidebar.expanded,
                    })}
                    onClick={() => {
                      setLayout({
                        ...layout,
                        leftSidebar: {
                          ...layout.leftSidebar,
                          expanded: !layout.leftSidebar.expanded,
                        },
                      });
                    }}
                    data-testid="toggle-workspace-left-sidebar"
                  >
                    <DocumentObjectExplorerCollapseIcon />
                  </div>
                </Tooltip>
                <Tooltip
                  title={`${
                    layout.results.expanded
                      ? t("workspace.tooltips.hide_results")
                      : t("workspace.tooltips.show_results")
                  } (${getShortcutHint(
                    SHORTCUTS[ShortcutAction.ToggleResultsSection]
                  ).join(" + ")})`}
                  placement="top-start"
                >
                  <div
                    className={classNames(styles.toggle, {
                      [styles.active]: layout.results.expanded,
                    })}
                    data-testid="toggle-workspace-results"
                    onClick={() => {
                      setLayout({
                        ...layout,
                        results: {
                          ...layout.results,
                          expanded: !layout.results.expanded,
                        },
                      });
                    }}
                  >
                    <DocumentOutputCollpaseIcon />
                  </div>
                </Tooltip>
              </div>
              {isConnectWizardEnabled && (
                <div>
                  <Tooltip
                    title={t("connect_modal.button_tooltip")}
                    placement="top-start"
                  >
                    <div>
                      {layout.leftSidebar.expanded ? (
                        <Button
                          className={styles.connectButton}
                          text={t("connect_modal.button_text")}
                          template={ButtonTemplate.Secondary}
                          size={ButtonSize.Small}
                          startIcon={
                            <ConnectIcon
                              className={styles.connectIconInButton}
                            />
                          }
                          onClick={() => setIsConnectModalOpen(true)}
                          dataTestid="connect-button"
                        />
                      ) : (
                        <ConnectIcon
                          className={styles.connectIcon}
                          onClick={() => setIsConnectModalOpen(true)}
                          data-test-id="connect-icon"
                        />
                      )}
                    </div>
                  </Tooltip>
                </div>
              )}
            </div>
          </LeftSidebarContext.Provider>
        </div>
      </Resizable>
      {isConnectModalOpen && (
        <ConnectModalLoader
          onClose={() => setIsConnectModalOpen(false)}
          defaultDatabase={
            activeDocument?.context?.database?.name &&
            typeof activeDocument.context.database.name === "string"
              ? activeDocument.context.database.name
              : undefined
          }
          defaultEngine={
            activeDocument?.context?.engine?.name &&
            typeof activeDocument.context.engine.name === "string"
              ? activeDocument.context.engine.name
              : undefined
          }
        />
      )}
    </div>
  );
};

export default LeftSidebar;
